/**
 * Create Store function for Reflux replacement.
 * Creates a store with state management, action listeners, and lifecycle methods.
 *
 * @module reflux/createStore
 */

import { createPublisher } from './publisher.js';
import { createListenerMethods } from './listener.js';
import { bindListenables } from './listenables.js';
import { STORE_INTERNAL_PROPS } from './constants.js';

// Re-export for backward compatibility (DevTools and other consumers)
export { STORE_INTERNAL_PROPS };

// Counter for generating unique store names when none provided
let storeCounter = 0;

// Store registry for on-demand state access (used by DevTools)
const storeRegistry = new Map();

/**
 * Get a store by name.
 * Used by DevTools for on-demand state inspection.
 *
 * @param {string} name - Store name
 * @returns {Object|undefined} Store instance or undefined if not found
 */
export const getStoreByName = (name) => storeRegistry.get(name);

/**
 * Get all registered store names.
 * Used by DevTools to list available stores.
 *
 * @returns {string[]} Array of store names
 */
export const getAllStoreNames = () => [...storeRegistry.keys()];

/**
 * Unregister a store from the registry.
 * Use this to clean up dynamically created stores and prevent memory leaks.
 *
 * @param {string} name - Store name to unregister
 * @returns {boolean} True if store was found and removed, false otherwise
 *
 * @example
 * // Create a temporary store
 * const TempStore = createStore({ storeName: 'TempStore', ... });
 *
 * // Later, when done with the store
 * unregisterStore('TempStore');
 */
export const unregisterStore = (name) => storeRegistry.delete(name);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Binds all definition methods to the store context.
 * This ensures methods can be called without store context: store.getState()
 *
 * @param {Object} store - Store instance
 * @param {Object} definition - Original store definition
 */
const bindMethodsToStore = (store, definition) => {
  for (const name in definition) {
    if (typeof definition[name] === 'function' &&
        Object.prototype.hasOwnProperty.call(definition, name)) {
      store[name] = definition[name].bind(store);
    }
  }
};

/**
 * Attaches publisher methods to a store.
 *
 * @param {Object} store - Store instance
 * @param {Object} publisher - Publisher instance
 */
const attachPublisherToStore = (store, publisher) => {
  store.trigger = publisher.trigger.bind(publisher);
  store.triggerAsync = publisher.triggerAsync.bind(publisher);
  store.listen = publisher.listen.bind(publisher);
  store.emitter = publisher.emitter;
  store.eventLabel = publisher.eventLabel;
};

/**
 * Handles listenables binding after init (sync or async).
 * Binds any listenables that were set during init() but not in the definition.
 *
 * @param {Object} store - Store instance
 * @param {Object} definition - Original store definition
 */
const bindListenablesAfterInit = (store, definition) => {
  if (store.listenables && store.listenables !== definition.listenables) {
    try {
      bindListenables(store, store.listenables);
    } catch (error) {
      console.warn('Warning during listenables binding:', error.message);
    }
  }
};

/**
 * Handles async init() result and dynamic listenables binding.
 * Supports stores that set this.listenables inside init() method.
 * Sets store.initError if async initialization fails.
 *
 * @param {*} initResult - Return value from store.init()
 * @param {Object} store - Store instance
 * @param {Object} definition - Original store definition
 */
const handleAsyncInit = (initResult, store, definition) => {
  if (initResult && typeof initResult.then === 'function') {
    // Async init pattern (e.g., SelectionStore, ActiveBlockContainerStore)
    initResult
      .then(() => {
        bindListenablesAfterInit(store, definition);
        store.initError = null; // Explicitly set to null on success
      })
      .catch(error => {
        store.initError = error; // Expose error for consuming code to check
        console.error(`Store async init() error: ${error.message}`);
      });
  } else {
    // Synchronous init pattern (most stores)
    bindListenablesAfterInit(store, definition);
  }
};

/**
 * @typedef {Object} Store
 * @property {import('eventemitter3').default} emitter - The underlying event emitter
 * @property {string} eventLabel - The event name used for emissions
 * @property {Function} trigger - Trigger event to all listeners synchronously
 * @property {Function} triggerAsync - Trigger event asynchronously on next tick
 * @property {Function} listen - Subscribe to events from this store
 * @property {Function} listenTo - Listen to a publisher (action or store)
 * @property {Function} stopListeningTo - Stop listening to a specific listenable
 * @property {Function} stopListeningToAll - Stop all subscriptions
 * @property {Function} hasListener - Check if listening to a listenable
 * @property {Array} subscriptions - Active listener subscriptions
 * @property {Function} [init] - Optional lifecycle method called on creation
 * @property {Function} [getInitialState] - Optional method to return initial state
 * @property {Error|null} [initError] - Error from async init() failure, null on success, undefined if sync init
 */

/**
 * Creates a store with state and automatic action binding.
 *
 * @param {Object} definition - Store configuration object containing lifecycle methods, listenables, and any custom methods/properties
 * @param {string} [definition.storeName] - Optional store name for DevTools and memory tracking
 * @param {string} [definition.displayName] - Alternative to storeName
 * @param {Function} [definition.init] - Lifecycle method called on creation
 * @param {Function} [definition.getInitialState] - Returns initial state for components
 * @param {Array|Object} [definition.listenables] - Actions to automatically bind to on<ActionName> methods
 * @returns {Store} Store instance with trigger(), listen(), and custom methods
 *
 * @example
 * const UserStore = createStore({
 *   listenables: [UserActions],
 *
 *   init() {
 *     this.state = { user: null, error: null };
 *   },
 *
 *   onLogin(credentials) {
 *     this.state = { ...this.state, error: null };
 *   },
 *
 *   onLoginComplete(userData) {
 *     this.state = { ...this.state, user: userData };
 *     this.trigger(this.state);
 *   },
 *
 *   onLoginFail(error) {
 *     this.state = { ...this.state, error: error.message };
 *     this.trigger(this.state);
 *   },
 *
 *   getInitialState() {
 *     return this.state;
 *   }
 * });
 *
 * // Component usage
 * const unlisten = UserStore.listen((state) => {
 *   console.log('State updated:', state);
 * });
 */
export const createStore = (definition) => {
  if (!definition || typeof definition !== 'object') {
    throw new TypeError('createStore: definition must be an object');
  }

  const storeName = definition.storeName || definition.displayName || `Store_${++storeCounter}`;
  const publisher = createPublisher('update', storeName, 'store');

  /** @type {any} */
  const store = { ...definition };

  // Setup store with all required methods
  bindMethodsToStore(store, definition);
  attachPublisherToStore(store, publisher);
  Object.assign(store, createListenerMethods(store));

  // CRITICAL: init() before listenables (state must exist before handlers run)
  if (typeof store.init === 'function') {
    try {
      const initResult = store.init();
      handleAsyncInit(initResult, store, definition);
    } catch (error) {
      throw new Error(`Store init() failed: ${error.message}`);
    }
  }

  // Bind definition listenables AFTER init() completes
  if (definition.listenables) {
    try {
      bindListenables(store, definition.listenables);
    } catch (error) {
      console.warn('Warning during listenables binding:', error.message);
    }
  }

  storeRegistry.set(storeName, store);
  return store;
};
